import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, ShieldCheck, Lock, CheckCircle2, AlertCircle, ArrowLeft, ArrowRight,
  Upload, FileText, Eraser, Download,
} from "lucide-react";
import { formatApiError } from "../lib/api";
import { portalService } from "../services/portal.service";
import { track, EVT } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function FirmHeader({ firm }) {
  const color = firm?.branding?.primary_color || "#4F46E5";
  return (
    <header className="border-b border-border2 bg-white" data-testid="portal-firm-header">
      <div className="max-w-2xl mx-auto px-6 py-5 flex items-center gap-4">
        {firm?.branding?.logo_data_url ? (
          <img src={firm.branding.logo_data_url} alt={firm.name} className="h-10 max-w-[200px] object-contain" />
        ) : (
          <div className="w-10 h-10 rounded-md flex items-center justify-center text-white font-bold" style={{ background: color }}>
            {firm?.name?.[0] || "F"}
          </div>
        )}
        <div>
          <div className="t-h4">{firm?.name}</div>
          {firm?.fca_number && <div className="t-caption">FCA: {firm.fca_number}</div>}
        </div>
      </div>
    </header>
  );
}

function PortalFooter() {
  // White-label by default, but a discreet trust signal reassures the client
  // the platform is regulated-grade.
  return (
    <footer className="border-t border-border2 bg-white" data-testid="portal-footer">
      <div className="max-w-2xl mx-auto px-6 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-slate2">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5"><Lock size={12} className="text-emerald2-dark" />Bank-grade encryption</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald2-dark" />FCA-aware</span>
        </div>
        <div>
          Secured by <Link to="/security" className="text-indigo2 hover:underline font-medium">Compliche</Link>
        </div>
      </div>
    </footer>
  );
}

function ProgressHeader({ flow, progress, color, onSaveAndExit, autoSaveState }) {
  const total = flow?.steps?.length || 1;
  const cur = Math.min((progress?.current_step ?? 0), total);
  const pct = Math.round((cur / total) * 100);
  const stepName = flow?.steps?.[cur]?.name || "Complete";
  return (
    <div className="bg-white border-b border-border2 sticky top-0 z-10" data-testid="portal-progress">
      <div className="max-w-2xl mx-auto px-6 py-3">
        <div className="flex justify-between items-center mb-2">
          <div className="t-caption">Step {Math.min(cur + 1, total)} of {total} · <span className="text-ink font-medium">{stepName}</span></div>
          <div className="flex items-center gap-3">
            {autoSaveState === "saving" && <span className="t-caption flex items-center gap-1 text-slate2" data-testid="autosave-saving"><Loader2 size={11} className="animate-spin" />Saving…</span>}
            {autoSaveState === "saved" && <span className="t-caption flex items-center gap-1 text-emerald2-dark" data-testid="autosave-saved"><CheckCircle2 size={11} />Saved</span>}
            <button onClick={onSaveAndExit} className="t-caption text-indigo2 hover:underline">Save and continue later</button>
          </div>
        </div>
        <div className="h-1.5 bg-border2 rounded-full overflow-hidden">
          <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
    </div>
  );
}

function StepWelcome({ step, color, onNext }) {
  const c = step.config || {};
  return (
    <div className="text-center" data-testid="step-welcome">
      <h1 className="t-display mb-4">{c.heading || "Welcome"}</h1>
      <p className="t-body-lg text-slate2 mb-8 whitespace-pre-wrap">{c.body}</p>
      <button onClick={onNext} className="btn btn-lg text-white" style={{ background: color }} data-testid="welcome-begin">
        {c.button_label || "Begin"} <ArrowRight size={18} />
      </button>
    </div>
  );
}

function StepQuestionnaire({ step, color, onComplete, savedAnswers = {} }) {
  const c = step.config || {};
  const questions = c.questions || [];
  const oneAtATime = c.one_at_a_time;
  const [answers, setAnswers] = useState(savedAnswers);
  const [idx, setIdx] = useState(0);
  const { setDraft } = React.useContext(PortalDraftContext);

  // Publish current answers so the 30s auto-save loop can persist them.
  useEffect(() => { setDraft(step.id, answers); }, [answers, step.id, setDraft]);

  const set = (qid, val) => setAnswers({ ...answers, [qid]: val });

  const renderInput = (q) => {
    const v = answers[q.id] || "";
    const upd = (val) => set(q.id, val);
    switch (q.type) {
      case "long_text": return <textarea className="textarea2" rows={4} value={v} onChange={(e) => upd(e.target.value)} />;
      case "number": return <input type="number" className="input2" value={v} onChange={(e) => upd(e.target.value)} />;
      case "date": return <input type="date" className="input2" value={v} onChange={(e) => upd(e.target.value)} />;
      case "email": return <input type="email" className="input2" value={v} onChange={(e) => upd(e.target.value)} />;
      case "phone": return <input type="tel" className="input2" value={v} onChange={(e) => upd(e.target.value)} />;
      case "yes_no": return (
        <div className="flex gap-2">
          {["Yes", "No"].map((opt) => <button key={opt} type="button" onClick={() => upd(opt)} className={`btn ${v === opt ? "btn-primary" : "btn-secondary"}`} style={v === opt ? { background: color } : {}}>{opt}</button>)}
        </div>
      );
      case "single_choice": return (
        <div className="space-y-2">{(q.options || []).map((opt) => <label key={opt} className="flex items-center gap-2 p-3 border border-border2 rounded-md cursor-pointer hover:border-indigo2"><input type="radio" name={q.id} value={opt} checked={v === opt} onChange={() => upd(opt)} className="accent-indigo2" />{opt}</label>)}</div>
      );
      case "multi_choice": {
        const arr = Array.isArray(v) ? v : [];
        return <div className="space-y-2">{(q.options || []).map((opt) => <label key={opt} className="flex items-center gap-2 p-3 border border-border2 rounded-md cursor-pointer hover:border-indigo2"><input type="checkbox" checked={arr.includes(opt)} onChange={(e) => upd(e.target.checked ? [...arr, opt] : arr.filter((x) => x !== opt))} className="w-4 h-4 accent-indigo2" />{opt}</label>)}</div>;
      }
      case "scale": return (
        <div className="flex gap-1">{Array.from({ length: 10 }).map((_, i) => <button key={i} type="button" onClick={() => upd(i + 1)} className={`w-10 h-10 rounded-md font-semibold ${Number(v) === i + 1 ? "text-white" : "bg-surface text-slate2"}`} style={Number(v) === i + 1 ? { background: color } : {}}>{i + 1}</button>)}</div>
      );
      default: return <input className="input2" value={v} onChange={(e) => upd(e.target.value)} />;
    }
  };

  const allValid = () => questions.every((q) => !q.required || (answers[q.id] !== undefined && answers[q.id] !== ""));

  if (oneAtATime) {
    const q = questions[idx];
    if (!q) return null;
    const canNext = !q.required || (answers[q.id] !== undefined && answers[q.id] !== "");
    return (
      <div data-testid="step-questionnaire-single">
        <div className="t-caption mb-4 text-right">Question {idx + 1} of {questions.length}</div>
        <h2 className="t-h2 mb-6">{q.text}{q.required && <span className="text-rose2 ml-1">*</span>}</h2>
        {renderInput(q)}
        <div className="flex justify-between mt-8">
          <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} className="btn btn-secondary"><ArrowLeft size={16} />Back</button>
          {idx < questions.length - 1 ? (
            <button onClick={() => setIdx(idx + 1)} disabled={!canNext} className="btn text-white" style={{ background: color }} data-testid="q-continue">Continue <ArrowRight size={16} /></button>
          ) : (
            <button onClick={() => onComplete(answers)} disabled={!allValid()} className="btn text-white" style={{ background: color }} data-testid="q-submit">Submit <ArrowRight size={16} /></button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="step-questionnaire-all">
      {c.intro && <p className="t-body text-slate2 mb-6">{c.intro}</p>}
      <div className="space-y-6">
        {questions.map((q) => (
          <div key={q.id}>
            <label className="label2 mb-2">{q.text}{q.required && <span className="text-rose2 ml-1">*</span>}</label>
            {renderInput(q)}
          </div>
        ))}
      </div>
      <button onClick={() => onComplete(answers)} disabled={!allValid()} className="btn text-white mt-8" style={{ background: color }} data-testid="q-submit-all">Submit answers <ArrowRight size={16} /></button>
    </div>
  );
}

function StepDocs({ step, color, onComplete }) {
  const c = step.config || {};
  const reqs = c.requests || [];
  const [uploaded, setUploaded] = useState({});
  const onFile = (idx, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 25 * 1024 * 1024) { alert("Max 25MB"); return; }
    // Store name only (we're not actually uploading binary in Phase 3)
    setUploaded({ ...uploaded, [idx]: { name: f.name, size: f.size, uploaded_at: new Date().toISOString() } });
  };
  const allRequired = reqs.every((r, i) => !r.required || uploaded[i]);
  return (
    <div data-testid="step-docs">
      {c.intro && <p className="t-body text-slate2 mb-6">{c.intro}</p>}
      <div className="space-y-4">
        {reqs.map((r, i) => (
          <div key={i} className="border border-border2 rounded-lg p-5">
            <div className="t-h4 mb-1">{r.name}{r.required && <span className="text-rose2 ml-1">*</span>}</div>
            {r.description && <p className="t-body-sm text-slate2 mb-3">{r.description}</p>}
            {uploaded[i] ? (
              <div className="flex items-center justify-between bg-emerald2-50 p-3 rounded-md">
                <div className="flex items-center gap-2 t-body-sm"><CheckCircle2 size={16} className="text-emerald2-dark" /><span>{uploaded[i].name}</span></div>
                <button onClick={() => { const u = { ...uploaded }; delete u[i]; setUploaded(u); }} className="t-caption text-rose2 hover:underline">Remove</button>
              </div>
            ) : (
              <label className="block border-2 border-dashed border-border2 rounded-md p-4 text-center cursor-pointer hover:border-indigo2">
                <Upload size={20} className="mx-auto text-slate2 mb-1" />
                <span className="t-body-sm">Drop or click to browse</span>
                <input type="file" className="hidden" onChange={(e) => onFile(i, e)} accept={(r.formats || ["pdf", "jpg", "png"]).map((f) => "." + f).join(",")} data-testid={`upload-${i}`} />
              </label>
            )}
          </div>
        ))}
      </div>
      <div className="security-card p-3 mt-5 t-body-sm flex items-center gap-2"><Lock size={14} className="text-emerald2-dark" />Your documents are encrypted in transit and at rest. Only your firm can access them.</div>
      <button onClick={() => onComplete({ uploaded })} disabled={!allRequired} className="btn text-white mt-6 btn-block" style={{ background: color }} data-testid="docs-continue">Continue <ArrowRight size={16} /></button>
    </div>
  );
}

function StepInfo({ step, color, onComplete }) {
  const c = step.config || {};
  const [acked, setAcked] = useState(false);
  return (
    <div data-testid="step-info">
      <h2 className="t-h2 mb-4">{c.heading}</h2>
      <div className="t-body text-slate2 whitespace-pre-wrap mb-6">{c.body}</div>
      {c.ack_required && (
        <label className="flex items-center gap-2 mb-6 t-body-sm p-3 border border-border2 rounded-md cursor-pointer">
          <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} className="w-4 h-4 accent-indigo2" data-testid="info-ack" />
          I have read and understood this information
        </label>
      )}
      <button onClick={() => onComplete({ acknowledged: acked })} disabled={c.ack_required && !acked} className="btn text-white btn-block" style={{ background: color }} data-testid="info-continue">Continue <ArrowRight size={16} /></button>
    </div>
  );
}

function StepConsent({ step, color, onComplete }) {
  const c = step.config || {};
  const items = c.items || [];
  const [accepted, setAccepted] = useState({});
  const allRequired = items.every((it, i) => !it.required || accepted[i]);
  return (
    <div data-testid="step-consent">
      {c.intro && <p className="t-body text-slate2 mb-6">{c.intro}</p>}
      <div className="space-y-3">
        {items.map((it, i) => (
          <label key={i} className="flex items-start gap-3 p-4 border border-border2 rounded-md cursor-pointer hover:border-indigo2">
            <input type="checkbox" checked={!!accepted[i]} onChange={(e) => setAccepted({ ...accepted, [i]: e.target.checked })} className="w-4 h-4 mt-0.5 accent-indigo2" data-testid={`consent-${i}`} />
            <span className="t-body-sm">{it.text}{it.required && <span className="text-rose2 ml-1">*</span>}</span>
          </label>
        ))}
      </div>
      <button onClick={() => onComplete({ consents: accepted })} disabled={!allRequired} className="btn text-white btn-block mt-6" style={{ background: color }} data-testid="consent-continue">Continue <ArrowRight size={16} /></button>
    </div>
  );
}

function StepSignature({ step, color, onSign, clientName }) {
  const c = step.config || {};
  const [mode, setMode] = useState("type"); // type | draw
  const [typedName, setTypedName] = useState(clientName || "");
  const [scrollDone, setScrollDone] = useState(!c.scroll_to_sign);
  const [readChecked, setReadChecked] = useState(false);
  const canvasRef = useRef();
  const [drawing, setDrawing] = useState(false);
  const [drawData, setDrawData] = useState("");
  const contentRef = useRef();

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setScrollDone(true);
  };

  const startDraw = (e) => {
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const drawMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0F1C2E";
    ctx.lineTo(x, y); ctx.stroke();
  };
  const endDraw = () => { setDrawing(false); if (canvasRef.current) setDrawData(canvasRef.current.toDataURL()); };
  const clearDraw = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setDrawData(""); };

  const canSign = readChecked && scrollDone && (mode === "type" ? typedName.length > 1 : drawData.length > 100);

  const submit = () => onSign({ method: mode, signature_data: mode === "type" ? typedName : drawData, full_name: typedName });

  return (
    <div data-testid="step-signature">
      <h2 className="t-h2 mb-2">{c.document_name || "Document"}</h2>
      <p className="t-body text-slate2 mb-4">{c.instructions}</p>

      <div ref={contentRef} onScroll={onScroll} className="border border-border2 rounded-md p-5 mb-4 bg-surface max-h-[320px] overflow-y-auto" data-testid="document-content">
        <h3 className="t-h4 mb-3">{c.document_name}</h3>
        <p className="t-body-sm mb-3">[This is a placeholder document. In the live product, a real PDF preview would render here. Scroll to the bottom to enable signing.]</p>
        {Array.from({ length: 8 }).map((_, i) => (
          <p key={i} className="t-body-sm text-slate2 mb-3">Section {i + 1}. Lorem ipsum dolor sit amet consectetur adipiscing elit. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
        ))}
        <p className="t-body-sm font-semibold mt-4">— End of document —</p>
      </div>

      <label className={`flex items-center gap-2 mb-5 t-body-sm p-3 border rounded-md ${scrollDone ? "border-border2 cursor-pointer" : "border-border2 opacity-50"}`}>
        <input type="checkbox" disabled={!scrollDone} checked={readChecked} onChange={(e) => setReadChecked(e.target.checked)} className="w-4 h-4 accent-indigo2" data-testid="read-checkbox" />
        I have read and understood this document
        {!scrollDone && <span className="t-caption text-amber2-700 ml-2">(scroll to enable)</span>}
      </label>

      {readChecked && (
        <>
          <div className="inline-flex bg-surface rounded-md p-1 mb-3" data-testid="sig-method">
            {(c.signature_method !== "draw") && (
              <button onClick={() => setMode("type")} className={`px-4 py-2 rounded-md t-body-sm ${mode === "type" ? "bg-white shadow-card" : "text-slate2"}`}>Type</button>
            )}
            {(c.signature_method !== "type") && (
              <button onClick={() => setMode("draw")} className={`px-4 py-2 rounded-md t-body-sm ${mode === "draw" ? "bg-white shadow-card" : "text-slate2"}`}>Draw</button>
            )}
          </div>

          {mode === "type" ? (
            <>
              <input className="input2 text-3xl mb-3" style={{ fontFamily: "'Brush Script MT', cursive", height: 64, fontSize: 32 }} placeholder="Type your full name" value={typedName} onChange={(e) => setTypedName(e.target.value)} data-testid="sig-typed" />
            </>
          ) : (
            <>
              <canvas
                ref={canvasRef}
                width={500}
                height={140}
                className="border border-border2 rounded-md bg-white w-full mb-2 touch-none"
                onMouseDown={startDraw} onMouseMove={drawMove} onMouseUp={endDraw} onMouseLeave={endDraw}
                onTouchStart={startDraw} onTouchMove={drawMove} onTouchEnd={endDraw}
                data-testid="sig-canvas"
              />
              <button onClick={clearDraw} className="btn btn-ghost btn-sm mb-3"><Eraser size={14} />Clear</button>
              <input className="input2 mb-3" placeholder="Print your full name" value={typedName} onChange={(e) => setTypedName(e.target.value)} />
            </>
          )}

          <button onClick={submit} disabled={!canSign} className="btn text-white btn-block btn-lg" style={{ background: color }} data-testid="sign-submit">Sign document <CheckCircle2 size={18} /></button>
        </>
      )}
    </div>
  );
}

function CompletionScreen({ firm, flow, color }) {
  React.useEffect(() => { track(EVT.client_portal_completed, { flow_id: flow?.id }); }, [flow]);
  return (
    <div className="text-center py-8" data-testid="completion-screen">
      <div className="w-24 h-24 rounded-full bg-emerald2-50 flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 size={56} className="text-emerald2-dark" />
      </div>
      <h1 className="t-display mb-3">You've completed your onboarding!</h1>
      <p className="t-body-lg text-slate2 mb-6 whitespace-pre-wrap">
        {flow?.settings?.completion_message || `Thank you. ${firm?.name} will review your information and be in touch shortly.`}
      </p>
      <div className="t-body text-slate2 mb-8">
        <strong>What happens next:</strong> {firm?.name} will be in touch within 2–5 business days.
      </div>
      {flow?.settings?.redirect_url && <a href={flow.settings.redirect_url} className="btn text-white" style={{ background: color }}>Continue to {firm?.name} →</a>}
    </div>
  );
}

// PortalDraftContext lets any step subcomponent publish its in-progress answers
// to a shared ref. The top-level ClientPortal then auto-saves that draft to the
// backend every 30s (debounced — only fires if something changed since last save).
export const PortalDraftContext = React.createContext({ setDraft: () => {} });

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState("idle"); // idle | saving | saved
  const [saveExitMsg, setSaveExitMsg] = useState(false);
  const draftRef = useRef({});  // { stepId: answersObject }
  const lastSavedRef = useRef("");
  const setDraft = React.useCallback((stepId, answers) => { draftRef.current[stepId] = answers; }, []);

  const load = async () => {
    try { const data = await portalService.load(token); setData(data); track(EVT.client_portal_opened, { state: data?.state }); }
    catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [token]);

  // Auto-save loop. Fires every 30s. Skips when nothing has changed since the
  // last successful save, so we don't hammer the backend with no-ops.
  useEffect(() => {
    if (!data || data.state === "complete" || data.state === "expired" || data.state === "revoked") return;
    const interval = setInterval(async () => {
      const step = data.flow?.steps?.[data.progress?.current_step];
      const draftForStep = step ? draftRef.current[step.id] : null;
      if (!step || !draftForStep || Object.keys(draftForStep).length === 0) return;
      const snapshot = JSON.stringify({ id: step.id, draftForStep });
      if (snapshot === lastSavedRef.current) return;
      try {
        setAutoSaveState("saving");
        await portalService.saveProgress(token, step.id, draftForStep, false);
        lastSavedRef.current = snapshot;
        setAutoSaveState("saved");
        setTimeout(() => setAutoSaveState((s) => s === "saved" ? "idle" : s), 2000);
      } catch {
        setAutoSaveState("idle");
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [data, token]);

  const advance = async (answers) => {
    setSaving(true);
    try {
      const step = data.flow.steps[data.progress.current_step];
      await portalService.saveProgress(token, step.id, answers, true);
      delete draftRef.current[step.id];  // clear once committed
      lastSavedRef.current = "";
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const signStep = async ({ method, signature_data, full_name }) => {
    setSaving(true);
    try {
      const step = data.flow.steps[data.progress.current_step];
      await portalService.sign(token, { step_id: step.id, signature_data, method, full_name });
      // Server now auto-completes the sig step — just reload state
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="card2 p-8 max-w-md text-center">
            <AlertCircle size={40} className="text-rose2 mx-auto mb-3" />
            <h2 className="t-h2 mb-2">Something went wrong</h2>
            <p className="t-body text-slate2">{error}</p>
          </div>
        </div>
        <PortalFooter />
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-surface"><Loader2 size={32} className="animate-spin text-indigo2" /></div>;
  }
  if (data.state === "expired") {
    return (
      <div className="min-h-screen flex flex-col bg-surface" data-testid="portal-expired">
        {data.firm && <FirmHeader firm={data.firm} />}
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="card2 p-8 max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-amber2-50 flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={28} className="text-amber2" />
            </div>
            <h2 className="t-h2 mb-2">This invitation has expired</h2>
            <p className="t-body text-slate2 mb-4">For your security, onboarding links expire after a set period.</p>
            <p className="t-body-sm text-slate2">
              Please contact{" "}
              <span className="font-medium text-ink">{data.firm?.name || "your firm"}</span>
              {data.firm?.compliance_contact_email && (
                <> at <a href={`mailto:${data.firm.compliance_contact_email}`} className="text-indigo2 hover:underline">{data.firm.compliance_contact_email}</a></>
              )}{" "}
              for a new invitation.
            </p>
          </div>
        </div>
        <PortalFooter />
      </div>
    );
  }
  if (data.state === "revoked") {
    return (
      <div className="min-h-screen flex flex-col bg-surface" data-testid="portal-revoked">
        {data.firm && <FirmHeader firm={data.firm} />}
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="card2 p-8 max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-rose2-50 flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={28} className="text-rose2" />
            </div>
            <h2 className="t-h2 mb-2">This invitation is no longer active</h2>
            <p className="t-body text-slate2 mb-4">It has been withdrawn or archived.</p>
            <p className="t-body-sm text-slate2">
              Please contact{" "}
              <span className="font-medium text-ink">{data.firm?.name || "your firm"}</span>
              {data.firm?.compliance_contact_email && (
                <> at <a href={`mailto:${data.firm.compliance_contact_email}`} className="text-indigo2 hover:underline">{data.firm.compliance_contact_email}</a></>
              )}{" "}
              for assistance.
            </p>
          </div>
        </div>
        <PortalFooter />
      </div>
    );
  }

  const color = data.firm?.branding?.primary_color || "#4F46E5";
  const isComplete = data.state === "complete" || (data.progress.completed_steps.length >= (data.flow?.steps?.length || 0));
  const currentStep = !isComplete ? data.flow?.steps?.[data.progress.current_step] : null;
  const clientName = `${data.client?.title ? data.client.title + " " : ""}${data.client?.first_name} ${data.client?.last_name}`;

  return (
    <PortalDraftContext.Provider value={{ setDraft }}>
    <div className="min-h-screen bg-surface" data-testid="client-portal">
      <FirmHeader firm={data.firm} />
      {!isComplete && <ProgressHeader flow={data.flow} progress={data.progress} color={color} onSaveAndExit={() => setSaveExitMsg(true)} autoSaveState={autoSaveState} />}
      <main className="max-w-2xl mx-auto px-6 py-10">
        <div className="card2 p-6 md:p-10">
          {isComplete ? (
            <CompletionScreen firm={data.firm} flow={data.flow} color={color} />
          ) : currentStep ? (
            <>
              {currentStep.type === "welcome" && <StepWelcome step={currentStep} color={color} onNext={() => advance({})} />}
              {currentStep.type === "questionnaire" && <StepQuestionnaire step={currentStep} color={color} onComplete={advance} savedAnswers={data.progress.answers?.[currentStep.id] || {}} />}
              {currentStep.type === "document_request" && <StepDocs step={currentStep} color={color} onComplete={advance} />}
              {currentStep.type === "information" && <StepInfo step={currentStep} color={color} onComplete={advance} />}
              {currentStep.type === "consent" && <StepConsent step={currentStep} color={color} onComplete={advance} />}
              {currentStep.type === "e_signature" && <StepSignature step={currentStep} color={color} onSign={signStep} clientName={clientName} />}
              {currentStep.type === "identity_verification" && <StepDocs step={currentStep} color={color} onComplete={advance} />}
            </>
          ) : <Loader2 size={32} className="animate-spin text-indigo2 mx-auto" />}
        </div>

        <div className="mt-6 flex items-center justify-center gap-4 t-caption">
          <span className="inline-flex items-center gap-1"><Lock size={12} className="text-emerald2-dark" />Bank-grade encryption</span>
          <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-emerald2-dark" />FCA-aware</span>
        </div>
      </main>

      {saving && <div className="fixed bottom-4 right-4 card2 px-4 py-2 flex items-center gap-2"><Loader2 size={14} className="animate-spin text-indigo2" /><span className="t-body-sm">Saving…</span></div>}

      {saveExitMsg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSaveExitMsg(false)} />
          <div className="relative card2 p-6 max-w-md">
            <CheckCircle2 size={32} className="text-emerald2-dark mb-3" />
            <h3 className="t-h3 mb-2">Your progress is saved</h3>
            <p className="t-body text-slate2 mb-4">Use the same link in your email to continue where you left off.</p>
            <button onClick={() => setSaveExitMsg(false)} className="btn btn-primary btn-block" style={{ background: color }}>Got it</button>
          </div>
        </div>
      )}

      <PortalFooter />
    </div>
    </PortalDraftContext.Provider>
  );
}
